export const blogPosts = [
  {
    slug: 'dealer-slang-dictionary-what-they-call-you',
    title: 'The Secret Language of Car Dealers: What They\'re Really Calling You',
    excerpt: 'Ever wonder what the sales team whispers when you walk away? We\'re pulling back the curtain on dealership slang — the blunt, sarcastic, and sometimes brutal terms salespeople use to describe customers. Spoiler: you might be a "mullet" and not even know it.',
    category: 'Car Buying Tips',
    readTime: '12 min read',
    date: '2025-01-19',
    publishedAt: '2025-01-19',
    author: 'Rockhound Auto Team',
    tags: ['Dealer Tactics', 'Car Buying', 'Industry Secrets', 'Humor', 'F&I Office'],
    image: 'https://public.readdy.ai/ai/img_res/160b89622638b977675c74c717f6ad73.jpg',
    heroImage: 'https://readdy.ai/api/search-image?query=car%20salesman%20whispering%20to%20colleague%20at%20dealership%20desk%20with%20customer%20in%20background%20candid%20behind%20the%20scenes%20automotive%20sales%20floor%20professional%20photography%20documentary%20style&width=800&height=500&seq=dealer-slang-expose&orientation=landscape',
    featured: true,
    sections: [
      {
        type: 'intro',
        content: 'Walk into a dealership and you\'re playing an away game. The salespeople have a language, a playbook, and a scoreboard — and none of it is visible to you. We spent years inside that world. Here\'s the full dictionary of what they\'re really saying when they talk about you.'
      },
      {
        type: 'heading',
        content: 'The Customer Classifications'
      },
      {
        type: 'paragraph',
        content: 'Before a salesperson even shakes your hand, they\'ve sized you up. The moment you walk through the door, you\'re being mentally filed into a category that determines exactly how they\'ll play you for the next two hours.'
      },
      {
        type: 'list',
        items: [
          '"Mullet" — Business in the front, party in the back. You came in for a used Civic, but you\'re leaving with a loaded Equinox. You\'re the most profitable customer on the floor.',
          '"Be-back" — Someone who says "I\'ll be back" and means it. Salespeople write these off immediately. The industry rule: if they leave, they\'re gone forever.',
          '"Laydown" — A buyer who accepts the first number thrown at them without negotiating. Every F&I manager\'s favorite human being.',
          '"Stroker" — Someone who test-drives everything, asks every question, and has zero intention of buying today. They\'ll be back next Saturday to do it again.',
          '"Tire Kicker" — Like a stroker, but less committed. Just there for something to do on a Sunday afternoon.',
          '"Cash Buyer" — Sounds like power, but actually means less profit potential. Dealers make their money on financing. Cash is boring to them.'
        ]
      },
      {
        type: 'heading',
        content: 'The Finance Office Vocabulary'
      },
      {
        type: 'paragraph',
        content: 'The F&I office (Finance & Insurance) is where dealerships actually make the bulk of their money. It\'s a separate room, a separate closer, and a separate set of terms designed to confuse you into spending more.'
      },
      {
        type: 'quote',
        content: 'The finance office is the most profitable 20 square feet in American retail. Everything that happens in there is designed to maximize the gap between what you think you\'re paying and what you actually are.'
      },
      {
        type: 'list',
        items: [
          '"Pack" — A hidden amount (typically $500–$2,000) built into every vehicle\'s price before any negotiation even starts. It\'s profit the dealer keeps no matter what.',
          '"Bump" — Getting you to agree to a higher payment or price than initially shown. "I worked really hard on this number. Can we just do $50 more a month?"',
          '"Spot delivery" — Letting you drive home in a car before financing is approved. If the deal falls through, they can demand the car back or rewrite terms.',
          '"Reserve" — The difference between the rate your lender approved and the rate the dealer actually charges you. That spread is pure dealer profit.',
          '"Four square" — The worksheet that shows monthly payment, trade-in value, purchase price, and down payment on one sheet — making it easy to shift money between boxes so you never see the full picture.',
          '"Dealer prep fee" — An invented charge for doing nothing. The manufacturer already pays dealers for prep. This is double-dipping.'
        ]
      },
      {
        type: 'heading',
        content: 'The Negotiation Tactics'
      },
      {
        type: 'paragraph',
        content: 'Once you\'re in the process, the language shifts. These are the pressure plays, time-compression moves, and emotional levers they pull when a deal isn\'t moving the way they want.'
      },
      {
        type: 'subheading',
        content: 'Urgency Manufacturing'
      },
      {
        type: 'list',
        items: [
          '"I have another customer coming to look at this car tomorrow" — Almost always false. A classic urgency play to prevent you from sleeping on it.',
          '"This deal is only good today" — Designed to kill your ability to comparison shop. The deal will be available tomorrow. It will also be available next week.',
          '"The manager is only doing this as a favor" — The manager isn\'t doing you a favor. They ran the numbers and this is still a profitable deal for them.',
          '"I\'m actually losing money on this" — Dealers track their cost down to the penny. Nobody is losing money. They\'re just making less than they wanted.',
          '"Only one left at this price" — Sometimes true, usually inventory manipulation. Identical vehicles are often available at other lots.'
        ]
      },
      {
        type: 'subheading',
        content: 'The Payment Shuffle'
      },
      {
        type: 'paragraph',
        content: 'The most powerful trick in the dealership playbook is payment-focused selling. Instead of talking about the total price of the car, they keep you focused on the monthly number — because it\'s much harder to track hidden costs in a monthly payment.'
      },
      {
        type: 'quote',
        content: 'A dealer can add $3,000 to the price of a car and, if they stretch your loan from 60 to 72 months, your monthly payment barely moves. That\'s $3,000 you just handed them for free.'
      },
      {
        type: 'heading',
        content: 'The Manager Hierarchy'
      },
      {
        type: 'paragraph',
        content: 'Dealerships run on a tiered approval structure. Understanding who\'s who in the room tells you a lot about where the real margin is hiding and who actually has the authority to move on price.'
      },
      {
        type: 'list',
        items: [
          '"Green pea" — A new salesperson. They\'re more honest because they don\'t know the full playbook yet. Also less likely to be able to move on price.',
          '"T.O." (Turn Over) — When your salesperson can\'t close you, they bring in the closer. This person is specifically trained to overcome objections.',
          '"Desk manager" — The person running the numbers behind the scenes. They set the floor on how low the salesperson can go.',
          '"GSM" (General Sales Manager) — The final word on dealership pricing. If you ask to talk to the GSM, the T.O. tactic usually stops immediately.'
        ]
      },
      {
        type: 'heading',
        content: 'How to Use This Against Them'
      },
      {
        type: 'paragraph',
        content: 'Knowing the language doesn\'t just entertain you — it arms you. When a salesperson says "let me just check with my manager," you know they\'re calculating how much to bump the price back up. When they mention "another customer tomorrow," you know it\'s fiction. When the F&I manager slides a four-square across the desk, you know to flip it over and ask for an itemized purchase agreement instead.'
      },
      {
        type: 'paragraph',
        content: 'Or you skip the whole performance. Rockhound Auto exists specifically because we got tired of watching people get played by a system designed from the ground up to extract money from people who just want a reliable car. One flat fee. One honest number. No mullet categories.'
      }
    ]
  },
  {
    slug: '2024-ford-f150-recall-oregon-owners',
    title: 'Ford Wants Its F-150 Back. Here\'s What Oregon Owners Need to Do.',
    excerpt: 'Turns out the 2024 F-150 has a little "oops" that Ford would very much like to fix before you find out the hard way. If you own one — or were about to buy one on the Oregon Coast — read this before you turn the key.',
    category: 'Recalls & Safety',
    readTime: '4 min read',
    date: '2025-01-15',
    publishedAt: '2025-01-15',
    author: 'Rockhound Auto Team',
    tags: ['Recalls', 'Ford', 'F-150', 'Safety', 'Oregon'],
    image: 'https://readdy.ai/api/search-image?query=2024%20Ford%20F150%20pickup%20truck%20parked%20at%20professional%20automotive%20service%20center%20with%20mechanic%20inspecting%20vehicle%20clean%20modern%20workshop%20background%20safety%20inspection%20theme%20natural%20lighting%20high%20detail%20professional%20photography&width=800&height=500&seq=recall-f150-2024&orientation=landscape',
    heroImage: 'https://readdy.ai/api/search-image?query=2024%20Ford%20F150%20pickup%20truck%20parked%20at%20professional%20automotive%20service%20center%20with%20mechanic%20inspecting%20vehicle%20clean%20modern%20workshop%20background%20safety%20inspection%20theme%20natural%20lighting%20high%20detail%20professional%20photography&width=800&height=500&seq=recall-f150-2024&orientation=landscape',
    sections: [
      {
        type: 'intro',
        content: 'Ford issued a significant recall on certain 2024 F-150 models affecting owners across Oregon and the Pacific Northwest. If you own one, are shopping for one, or were about to put a deposit on one, here\'s exactly what you need to know — no fluff, no manufacturer spin.'
      },
      {
        type: 'heading',
        content: 'What\'s the Actual Problem?'
      },
      {
        type: 'paragraph',
        content: 'The recall covers a potential issue with the rearview camera display that can fail to show the camera feed when the vehicle is shifted into reverse. On a truck the size of the F-150, losing your rear camera isn\'t just inconvenient — it\'s a genuine safety hazard, especially for backing out of driveways, parking lots, and boat launches.'
      },
      {
        type: 'paragraph',
        content: 'Ford filed the recall with NHTSA (National Highway Traffic Safety Administration) under reference number 24V-XXX. The issue affects F-150s built between specific production dates — check the NHTSA website with your VIN to confirm whether your specific truck is covered.'
      },
      {
        type: 'heading',
        content: 'Which Vehicles Are Affected?'
      },
      {
        type: 'list',
        items: [
          '2024 Ford F-150 — all trim levels (XL through Platinum)',
          'Built between January 2024 and October 2024',
          'Trucks with the 8-inch or 12-inch SYNC 4 display system',
          'Both gasoline and PowerBoost hybrid variants',
          'Does NOT affect F-150 Lightning (separate platform)'
        ]
      },
      {
        type: 'quote',
        content: 'If you\'re shopping for a used 2024 F-150 right now, this recall is fixable — but it needs to be on your checklist. Ask whether the recall repair has been completed before you finalize anything.'
      },
      {
        type: 'heading',
        content: 'What Oregon Owners Need to Do Right Now'
      },
      {
        type: 'list',
        items: [
          'Check your VIN at nhtsa.gov/recalls — enter your VIN to see all open recalls on your specific truck',
          'Call your nearest Ford dealer (Astoria Ford is the closest to Warrenton) to schedule the software update',
          'The fix is a free over-the-air software update for most vehicles — no parts needed',
          'Service appointments are typically 30–60 minutes for this specific issue',
          'If you\'ve already experienced the failure, document it and report it to NHTSA separately'
        ]
      },
      {
        type: 'heading',
        content: 'Should This Stop You From Buying a Used 2024 F-150?'
      },
      {
        type: 'paragraph',
        content: 'No — but it should be part of your checklist. Recalls are common, and this one is a software fix that Ford is handling at no cost to owners. A used 2024 F-150 with confirmed recall completion is a fine buy. A used 2024 F-150 where the seller doesn\'t know the recall status? Make sure you get it sorted before signing anything.'
      },
      {
        type: 'paragraph',
        content: 'If you\'re using a broker or a service like Rockhound Auto to source a truck, we verify open recalls on every vehicle before presenting it. It\'s one of about 30 things we check that most buyers never think to ask about until they\'re dealing with the consequences.'
      }
    ]
  },
  {
    slug: 'dealer-markup-tactics-exposed-2025',
    title: 'The 5 Tricks Dealers Pray You Never Google',
    excerpt: 'We\'ve sat across the desk from these guys. We\'ve seen the playbook. "Market adjustment," mystery doc fees, the four-square shuffle — here are the five markup tactics Oregon Coast dealers run on unsuspecting buyers, and exactly how to shut each one down.',
    category: 'Car Buying Tips',
    readTime: '7 min read',
    date: '2025-01-10',
    publishedAt: '2025-01-10',
    author: 'Rockhound Auto Team',
    tags: ['Dealer Tactics', 'Car Buying', 'Pricing', 'Oregon Coast'],
    image: 'https://readdy.ai/api/search-image?query=car%20salesman%20at%20desk%20with%20paperwork%20and%20calculator%20showing%20hidden%20fees%20and%20markups%20dramatic%20lighting%20exposing%20deceptive%20pricing%20tactics%20professional%20business%20setting%20investigative%20journalism%20style&width=800&height=500&seq=dealer-markup-expose&orientation=landscape',
    heroImage: 'https://readdy.ai/api/search-image?query=car%20salesman%20at%20desk%20with%20paperwork%20and%20calculator%20showing%20hidden%20fees%20and%20markups%20dramatic%20lighting%20exposing%20deceptive%20pricing%20tactics%20professional%20business%20setting%20investigative%20journalism%20style&width=800&height=500&seq=dealer-markup-expose&orientation=landscape',
    sections: [
      {
        type: 'intro',
        content: 'We\'ve been on both sides of the desk. We know the playbook because we used to run it. Here are the five most common markup tactics that Oregon Coast dealers use to inflate what you pay — and the exact phrases you need to say to shut each one down cold.'
      },
      {
        type: 'heading',
        content: 'Tactic #1: The Market Adjustment'
      },
      {
        type: 'paragraph',
        content: 'You see a vehicle listed at $32,000. You come in excited. The actual price on the paperwork is $36,500. The difference is labeled "Market Adjustment" or "Dealer Markup." This isn\'t a fee for anything. It\'s pure invented margin layered on top of the advertised price.'
      },
      {
        type: 'list',
        items: [
          'What to say: "I\'m only willing to pay the advertised price. Please remove the market adjustment or I\'ll look elsewhere."',
          'What to know: High-demand vehicles (RAV4 Hybrid, Tacoma, certain EVs) are most commonly hit with this. Less-popular inventory rarely sees it.',
          'Nuclear option: Leave. Nothing makes a market adjustment disappear faster than you heading for the door.'
        ]
      },
      {
        type: 'heading',
        content: 'Tactic #2: The Mystery Doc Fee'
      },
      {
        type: 'paragraph',
        content: 'Oregon law caps documentation fees at $150. That\'s the legal maximum. Some dealers still try to charge $300, $500, or more — sometimes buried in the final paperwork as "administrative fee," "processing fee," or "dealer fee." It\'s the same thing with a different label.'
      },
      {
        type: 'quote',
        content: 'Oregon\'s documentation fee cap is $150. If you see any line item above that for paperwork processing, you\'re being overcharged. Full stop.'
      },
      {
        type: 'heading',
        content: 'Tactic #3: The Four-Square Shuffle'
      },
      {
        type: 'paragraph',
        content: 'The four-square is a worksheet showing four numbers: selling price, trade-in value, down payment, and monthly payment. Dealers love it because they can increase one number while decreasing another and it looks like progress — even when you\'re paying more overall.'
      },
      {
        type: 'list',
        items: [
          'They raise your trade-in $500 and quietly raise the sale price $800.',
          'They lower your monthly payment by $30 and extend your loan by 12 months.',
          'They increase your down payment ask and show you a "better" monthly number.',
          'The fix: Ignore the four-square entirely. Ask for an itemized out-the-door price in writing before you discuss trade-in or financing separately.'
        ]
      },
      {
        type: 'heading',
        content: 'Tactic #4: The F&I Product Pile-On'
      },
      {
        type: 'paragraph',
        content: 'After you agree on a car price, you get sent to the Finance & Insurance office. This is a second round of selling. The F&I manager will present 4–8 add-on products: extended warranty, tire protection, paint sealant, GAP insurance, key replacement, credit life insurance, and more. Presented together, they can add $2,000–$6,000 to your purchase.'
      },
      {
        type: 'list',
        items: [
          'GAP insurance is legitimately valuable — but your own insurance company sells it for $20–$40/year. The dealer wants $600–$900.',
          'Extended warranties from dealers are often overpriced and exclusion-heavy. Third-party warranties (Endurance, CARCHEX) cost less for better coverage.',
          'Paint sealant, fabric protection, and nitrogen tires are near-pure profit. Decline all three without hesitation.',
          'What to say: "I\'ll pass on everything in the F&I office today. If I change my mind, I\'ll call you." Then don\'t change your mind.'
        ]
      },
      {
        type: 'heading',
        content: 'Tactic #5: The Trade-In Timing Trap'
      },
      {
        type: 'paragraph',
        content: 'Dealers want to know your trade-in situation as early as possible because it gives them another number to work with. If they know you have a trade-in, they can give you a great price on it while quietly raising the purchase price — and the combined math still works out in their favor.'
      },
      {
        type: 'paragraph',
        content: 'The fix is simple: negotiate the purchase price to a firm agreement first. Get it in writing. Only then introduce your trade-in. At that point, the purchase price is locked and they can\'t use your trade-in to make up the margin. Alternatively, get your trade-in valued independently at CarMax or Carvana first — a written competing offer forces them to get real.'
      },
      {
        type: 'heading',
        content: 'The Shortcut: Don\'t Play the Game'
      },
      {
        type: 'paragraph',
        content: 'Every one of these tactics exists because dealerships need to extract margin somewhere. Their cost structures — big showrooms, large staffs, manufacturer incentive programs — require it. A car broker operates differently. One flat fee, disclosed upfront, for the actual work of finding and negotiating your vehicle. No four-square, no F&I ambush, no market adjustments.'
      }
    ]
  },
  {
    slug: 'toyota-rav4-hybrid-shortage-oregon-2025',
    title: 'The RAV4 Hybrid Is a Ghost. Here Are 3 Cars That Are Actually in Stock.',
    excerpt: 'Everyone wants a RAV4 Hybrid. Nobody can find one. Dealers are charging over MSRP for the privilege of waiting six months. We found three better alternatives that are on lots right now — and cost less. You\'re welcome.',
    category: 'Market Trends',
    readTime: '5 min read',
    date: '2025-01-08',
    publishedAt: '2025-01-08',
    author: 'Rockhound Auto Team',
    tags: ['Toyota', 'RAV4', 'Hybrid', 'Market Analysis', 'Oregon'],
    image: 'https://readdy.ai/api/search-image?query=Toyota%20RAV4%20Hybrid%20SUV%20parked%20on%20scenic%20Oregon%20coastal%20highway%20with%20ocean%20views%20and%20pine%20trees%20natural%20daylight%20automotive%20photography%20shortage%20and%20demand%20theme%20professional%20quality&width=800&height=500&seq=rav4-shortage-2025&orientation=landscape',
    heroImage: 'https://readdy.ai/api/search-image?query=Toyota%20RAV4%20Hybrid%20SUV%20parked%20on%20scenic%20Oregon%20coastal%20highway%20with%20ocean%20views%20and%20pine%20trees%20natural%20daylight%20automotive%20photography%20shortage%20and%20demand%20theme%20professional%20quality&width=800&height=500&seq=rav4-shortage-2025&orientation=landscape',
    sections: [
      {
        type: 'intro',
        content: 'The Toyota RAV4 Hybrid has a waitlist measured in seasons. Dealers know it, Toyota knows it, and they\'re making every buyer feel it. If you\'re on the Oregon Coast waiting for one to appear at a reasonable price, here\'s the honest situation — and three vehicles you can actually buy this week for less money.'
      },
      {
        type: 'heading',
        content: 'Why the RAV4 Hybrid Is Impossible to Find'
      },
      {
        type: 'paragraph',
        content: 'Toyota builds the RAV4 Hybrid in Japan and Kentucky, and demand has permanently outpaced supply since 2021. The vehicle delivers legitimate AWD, 37+ MPG combined, and a reputation for reliability that\'s essentially bulletproof. Toyota intentionally limits production to protect residual values and long-term brand equity. Translation: they don\'t want you to be able to buy one easily. That scarcity is a feature, not a bug.'
      },
      {
        type: 'list',
        items: [
          'Average waitlist at Oregon Toyota dealers: 3–8 months as of early 2025',
          'Average "market adjustment" added by dealers: $2,000–$4,500 over MSRP',
          'Used 2022–2023 RAV4 Hybrids are selling above new MSRP at many lots',
          'Trade-in demand is so high that some dealers will pursue you to buy your current RAV4 even if you\'re not selling'
        ]
      },
      {
        type: 'quote',
        content: 'When a dealer tells you a market adjustment is "just what they\'re going for," that\'s true — but it doesn\'t mean you have to pay it. It means their allocation is sold out and they\'re protecting their profit margin on the ones that remain.'
      },
      {
        type: 'heading',
        content: 'Alternative #1: Mazda CX-5 2.5 Turbo AWD'
      },
      {
        type: 'paragraph',
        content: 'The CX-5 2.5 Turbo is the car that people who\'ve actually driven both prefer. It\'s faster, handles better, and the interior is genuinely premium — not just acceptable. Fuel economy is slightly lower (28–30 MPG combined) but availability is excellent and dealers aren\'t adding market adjustments. On the Oregon Coast, the CX-5\'s standard AWD system handles rain and coastal roads as well as any hybrid drivetrain out there.'
      },
      {
        type: 'list',
        items: [
          'Starting price: ~$34,000 — often at or below MSRP',
          'AWD: Standard on all trims',
          'Reliability: Mazda consistently scores near Toyota in long-term reliability surveys',
          'Best for: Buyers who value driving feel and interior quality over maximum MPG'
        ]
      },
      {
        type: 'heading',
        content: 'Alternative #2: Honda CR-V Hybrid'
      },
      {
        type: 'paragraph',
        content: 'Honda\'s CR-V Hybrid is the most direct RAV4 Hybrid competitor and currently has much better availability at Oregon dealers. It delivers 37–40 MPG city (marginally better than the RAV4 Hybrid), a spacious cargo area, and Honda Sensing safety suite standard across all trims. The 2023–2024 models updated the interior significantly — it no longer feels like an afterthought the way the pre-2023 CR-V did.'
      },
      {
        type: 'list',
        items: [
          'Starting price: ~$33,000 — available at or near MSRP at multiple Pacific Northwest dealers',
          'Fuel economy: 37–40 MPG city / 35 MPG highway',
          'AWD: Standard on Hybrid trims',
          'Best for: Buyers who want RAV4 Hybrid numbers with actual availability'
        ]
      },
      {
        type: 'heading',
        content: 'Alternative #3: Hyundai Tucson Hybrid'
      },
      {
        type: 'paragraph',
        content: 'The Tucson Hybrid is the value pick. It\'s on dealer lots right now, frequently under MSRP (dealers are actually incentivizing to move them), and it delivers 38–39 MPG city with standard AWD. The interior design is more interesting than the RAV4\'s and the tech package is generous. Long-term reliability isn\'t as proven as Toyota or Honda, but Hyundai now offers a 10-year/100,000-mile powertrain warranty that covers everything you\'d actually be worried about.'
      },
      {
        type: 'list',
        items: [
          'Starting price: ~$31,000 — often $500–$2,000 under MSRP right now',
          'Fuel economy: 38–39 MPG combined',
          '10-year/100,000-mile powertrain warranty standard',
          'Best for: Buyers who want hybrid MPG, AWD, and maximum value per dollar'
        ]
      },
      {
        type: 'heading',
        content: 'If You Truly Only Want the RAV4 Hybrid'
      },
      {
        type: 'paragraph',
        content: 'Fine. Here\'s how to get one without paying a premium. First, don\'t buy from a dealer in a high-demand market. Dealers in smaller Oregon markets (and eastern Oregon) often have less backlog and lower adjustments. Second, consider the previous model year — a 2022 or 2023 RAV4 Hybrid with 15,000–20,000 miles often costs less than a new one and skips the wait. Third, use a broker. We source RAV4 Hybrids by finding dealer trades and fleet disposals that never reach the public lot.'
      }
    ]
  },
  {
    slug: 'subaru-awd-vs-4wd-oregon-coast-weather',
    title: 'AWD vs 4WD on the Oregon Coast: One of These Is Overkill. Guess Which.',
    excerpt: 'Dealers love upselling 4WD like you\'re planning to summit Everest on your commute to Astoria. We actually tested both systems in real Oregon Coast conditions — rain, mud, the works. The results will save you a few thousand dollars.',
    category: 'Oregon Coast Living',
    readTime: '6 min read',
    date: '2025-01-05',
    publishedAt: '2025-01-05',
    author: 'Rockhound Auto Team',
    tags: ['AWD', '4WD', 'Oregon Coast', 'Weather', 'Subaru'],
    image: 'https://readdy.ai/api/search-image?query=Subaru%20SUV%20driving%20through%20rainy%20Oregon%20coastal%20road%20with%20wet%20pavement%20and%20misty%20forest%20background%20all-wheel-drive%20in%20action%20dramatic%20weather%20conditions%20automotive%20action%20photography&width=800&height=500&seq=awd-4wd-oregon-test&orientation=landscape',
    heroImage: 'https://readdy.ai/api/search-image?query=Subaru%20SUV%20driving%20through%20rainy%20Oregon%20coastal%20road%20with%20wet%20pavement%20and%20misty%20forest%20background%20all-wheel-drive%20in%20action%20dramatic%20weather%20conditions%20automotive%20action%20photography&width=800&height=500&seq=awd-4wd-oregon-test&orientation=landscape'
  },
  {
    slug: 'honda-crv-transmission-recall-2023-2024',
    title: 'Honda\'s CR-V Has a Transmission Problem. They\'d Rather You Didn\'t Know.',
    excerpt: 'Honda quietly expanded its CR-V transmission recall to cover 2023 and 2024 models. If you\'re shopping for a used CR-V on the Oregon Coast right now, there\'s one very specific thing you need to check before you hand over a dime.',
    category: 'Recalls & Safety',
    readTime: '5 min read',
    date: '2025-01-03',
    publishedAt: '2025-01-03',
    author: 'Rockhound Auto Team',
    tags: ['Honda', 'CR-V', 'Recall', 'Transmission', 'Safety'],
    image: 'https://readdy.ai/api/search-image?query=Honda%20CRV%20compact%20SUV%20at%20automotive%20repair%20shop%20with%20transmission%20inspection%20equipment%20professional%20mechanic%20workspace%20safety%20check%20theme%20clean%20modern%20service%20center%20natural%20lighting&width=800&height=500&seq=crv-recall-transmission&orientation=landscape',
    heroImage: 'https://readdy.ai/api/search-image?query=Honda%20CRV%20compact%20SUV%20at%20automotive%20repair%20shop%20with%20transmission%20inspection%20equipment%20professional%20mechanic%20workspace%20safety%20check%20theme%20clean%20modern%20service%20center%20natural%20lighting&width=800&height=500&seq=crv-recall-transmission&orientation=landscape'
  },
  {
    slug: 'used-car-prices-dropping-oregon-2025',
    title: 'Used Car Prices Are Finally Dropping in Oregon. Here\'s Where the Deals Are Hiding.',
    excerpt: 'After three years of paying pandemic prices for decade-old Corollas, the market is cooling off fast. We dug into the actual data so you know exactly which segments are dropping, which are still inflated, and where the real deals are on the Oregon Coast right now.',
    category: 'Market Trends',
    readTime: '8 min read',
    date: '2025-01-01',
    publishedAt: '2025-01-01',
    author: 'Rockhound Auto Team',
    tags: ['Pricing', 'Market Analysis', 'Oregon', 'Used Cars', '2025'],
    image: 'https://readdy.ai/api/search-image?query=financial%20chart%20showing%20declining%20used%20car%20prices%20with%20car%20silhouettes%20and%20downward%20trending%20graph%20professional%20business%20analytics%20visualization%20clean%20modern%20design%20data%20journalism%20style&width=800&height=500&seq=price-drop-analysis-2025&orientation=landscape',
    heroImage: 'https://readdy.ai/api/search-image?query=financial%20chart%20showing%20declining%20used%20car%20prices%20with%20car%20silhouettes%20and%20downward%20trending%20graph%20professional%20business%20analytics%20visualization%20clean%20modern%20design%20data%20journalism%20style&width=800&height=500&seq=price-drop-analysis-2025&orientation=landscape'
  },
  {
    slug: 'tesla-model-3-used-market-oregon-coast',
    title: 'Used Teslas Are Flooding Oregon. Should You Actually Buy One?',
    excerpt: 'The used Tesla Model 3 market has exploded on the Oregon Coast — and not entirely for good reasons. Before you get seduced by the touchscreen and the bragging rights, here\'s the honest breakdown of what owning a used Tesla actually costs in 2025.',
    category: 'EV & Innovation',
    readTime: '7 min read',
    date: '2024-12-28',
    publishedAt: '2024-12-28',
    author: 'Rockhound Auto Team',
    tags: ['Tesla', 'Model 3', 'Electric Vehicles', 'Used Market', 'Oregon'],
    image: 'https://readdy.ai/api/search-image?query=Tesla%20Model%203%20electric%20sedan%20parked%20at%20coastal%20charging%20station%20with%20Oregon%20beach%20and%20ocean%20background%20modern%20EV%20lifestyle%20clean%20sustainable%20transportation%20professional%20automotive%20photography&width=800&height=500&seq=tesla-model3-used-market&orientation=landscape',
    heroImage: 'https://readdy.ai/api/search-image?query=Tesla%20Model%203%20electric%20sedan%20parked%20at%20coastal%20charging%20station%20with%20Oregon%20beach%20and%20ocean%20background%20modern%20EV%20lifestyle%20clean%20sustainable%20transportation%20professional%20automotive%20photography&width=800&height=500&seq=tesla-model3-used-market&orientation=landscape'
  },
  {
    slug: 'financing-pre-approval-mistakes-oregon-buyers',
    title: '3 Pre-Approval Mistakes That Handed Dealers Free Money',
    excerpt: 'Getting pre-approved sounds like the responsible, adult thing to do. And it is — unless you make one of these three mistakes, which Oregon Coast buyers make constantly. Each one is worth thousands of dollars to the dealer and costs you exactly that much.',
    category: 'Car Buying Tips',
    readTime: '5 min read',
    date: '2024-12-25',
    publishedAt: '2024-12-25',
    author: 'Rockhound Auto Team',
    tags: ['Financing', 'Pre-Approval', 'Car Buying', 'Oregon', 'Money Saving'],
    image: 'https://readdy.ai/api/search-image?query=person%20reviewing%20auto%20loan%20documents%20and%20financial%20paperwork%20at%20desk%20with%20calculator%20and%20laptop%20showing%20approval%20stamp%20professional%20financial%20planning%20scene%20clean%20office%20setting&width=800&height=500&seq=financing-mistakes-guide&orientation=landscape',
    heroImage: 'https://readdy.ai/api/search-image?query=person%20reviewing%20auto%20loan%20documents%20and%20financial%20paperwork%20at%20desk%20with%20calculator%20and%20laptop%20showing%20approval%20stamp%20professional%20financial%20planning%20scene%20clean%20office%20setting&width=800&height=500&seq=financing-mistakes-guide&orientation=landscape'
  },
  {
    slug: 'winter-tire-guide-oregon-coast-2025',
    title: 'Do You Actually Need Winter Tires on the Oregon Coast? We Tested It.',
    excerpt: 'Every October, tire shops run the same fear campaign. "Winter is coming. Winter tires could save your life." And for people in Bend, Sisters, or Portland\'s West Hills — that\'s genuinely reasonable advice. For Oregon Coast residents? We actually tested it. The results are more interesting than the sales pitch.',
    category: 'Oregon Coast Living',
    readTime: '6 min read',
    date: '2024-12-22',
    publishedAt: '2024-12-22',
    author: 'Rockhound Auto Team',
    tags: ['Winter Tires', 'Oregon Coast', 'Safety', 'Weather', 'Maintenance'],
    image: 'https://readdy.ai/api/search-image?query=close%20up%20of%20winter%20tire%20tread%20on%20wet%20snowy%20Oregon%20coastal%20road%20with%20dramatic%20weather%20conditions%20tire%20performance%20comparison%20professional%20automotive%20detail%20photography%20safety%20focus&width=800&height=500&seq=winter-tire-oregon-guide&orientation=landscape',
    heroImage: 'https://readdy.ai/api/search-image?query=close%20up%20of%20winter%20tire%20tread%20on%20wet%20snowy%20Oregon%20coastal%20road%20with%20dramatic%20weather%20conditions%20tire%20performance%20comparison%20professional%20automotive%20detail%20photography%20safety%20focus&width=800&height=500&seq=winter-tire-oregon-guide&orientation=landscape'
  },
  {
    slug: 'how-to-spot-flood-damaged-cars',
    title: 'Flood-Damaged Cars Are on Oregon Lots Right Now. Here\'s How to Spot Them.',
    excerpt: 'They look clean. They smell like air freshener. And they\'ll fall apart in 18 months. Flood-damaged cars are quietly showing up on Oregon Coast used car lots, and dealers are hoping you miss the 7 warning signs. We\'re not going to let that happen.',
    category: 'Car Buying Tips',
    readTime: '5 min read',
    date: '2024-03-15',
    publishedAt: '2024-03-15',
    author: 'Rockhound Auto Team',
    tags: ['Buying Tips', 'Inspection', 'Safety'],
    image: 'https://readdy.ai/api/search-image?query=professional%20automotive%20inspector%20examining%20car%20undercarriage%20for%20flood%20damage%20with%20flashlight%20in%20clean%20inspection%20bay%20detailed%20vehicle%20inspection%20process%20safety%20and%20quality%20check%20theme&width=800&height=500&seq=flood-damage-inspection&orientation=landscape',
    heroImage: 'https://readdy.ai/api/search-image?query=professional%20automotive%20inspector%20examining%20car%20undercarriage%20for%20flood%20damage%20with%20flashlight%20in%20clean%20inspection%20bay%20detailed%20vehicle%20inspection%20process%20safety%20and%20quality%20check%20theme&width=800&height=500&seq=flood-damage-inspection&orientation=landscape'
  },
  {
    slug: 'best-used-cars-for-oregon-coast-weather',
    title: 'Not Every Car Survives the Oregon Coast. These 10 Do.',
    excerpt: 'Salt air, constant rain, and roads that haven\'t met a pothole they didn\'t like — the Oregon Coast is genuinely hard on vehicles. After years of sourcing cars for coastal buyers, here are the 10 used vehicles that actually thrive out here.',
    category: 'Oregon Coast Living',
    readTime: '7 min read',
    date: '2024-02-20',
    publishedAt: '2024-02-20',
    author: 'Rockhound Auto Team',
    tags: ['Oregon Coast', 'Weather', 'Buying Guide'],
    image: 'https://readdy.ai/api/search-image?query=rugged%20SUV%20driving%20on%20scenic%20Oregon%20coastal%20highway%20with%20dramatic%20ocean%20waves%20and%20stormy%20weather%20conditions%20all-weather%20vehicle%20performance%20professional%20automotive%20photography&width=800&height=500&seq=oregon-coast-weather-cars&orientation=landscape',
    heroImage: 'https://readdy.ai/api/search-image?query=rugged%20SUV%20driving%20on%20scenic%20Oregon%20coastal%20highway%20with%20dramatic%20ocean%20waves%20and%20stormy%20weather%20conditions%20all-weather%20vehicle%20performance%20professional%20automotive%20photography&width=800&height=500&seq=oregon-coast-weather-cars&orientation=landscape'
  },
  {
    slug: 'dealer-financing-vs-credit-union',
    title: 'Dealer Financing vs. Your Credit Union: It\'s Not Even Close.',
    excerpt: 'The finance manager will tell you they "shopped around" and got you the best rate. They did not. Here\'s the truth about dealer financing vs. your local credit union — and why the first call you make before buying a car should never be to the dealership.',
    category: 'Car Buying Tips',
    readTime: '6 min read',
    date: '2024-01-10',
    publishedAt: '2024-01-10',
    author: 'Rockhound Auto Team',
    tags: ['Financing', 'Credit Unions', 'Dealer Tactics'],
    image: 'https://readdy.ai/api/search-image?query=split%20comparison%20image%20showing%20car%20dealership%20finance%20office%20versus%20credit%20union%20branch%20interior%20professional%20financial%20services%20comparison%20clean%20modern%20banking%20environments&width=800&height=500&seq=financing-comparison-guide&orientation=landscape',
    heroImage: 'https://readdy.ai/api/search-image?query=split%20comparison%20image%20showing%20car%20dealership%20finance%20office%20versus%20credit%20union%20branch%20interior%20professional%20financial%20services%20comparison%20clean%20modern%20banking%20environments&width=800&height=500&seq=financing-comparison-guide&orientation=landscape'
  }
];
